import { useEffect, useMemo, useState } from 'react';
import { EllipsisVertical } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Textarea } from '../../../components/ui/textarea';
import { truncateText } from '../../../utils/text';
import type { UnifiedAnnotation } from '../hooks/use-annotation-source';

const EXCERPT_MAX_LENGTH = 150;

interface AnnotationCardProps {
  annotation: UnifiedAnnotation;
  onNavigate: (annotation: UnifiedAnnotation) => void;
  onUpdateNote: (id: string, note: string) => Promise<void>;
  onDelete: (annotation: UnifiedAnnotation) => void;
}

function shouldIgnoreCardNavigation(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return Boolean(target.closest('button, textarea, input, [role="menuitem"]'));
}

export default function AnnotationCard({
  annotation,
  onNavigate,
  onUpdateNote,
  onDelete,
}: Readonly<AnnotationCardProps>): JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const [draftNote, setDraftNote] = useState(annotation.note);

  useEffect(() => {
    setDraftNote(annotation.note);
  }, [annotation.note]);

  const excerpt = useMemo(
    () => truncateText(annotation.messageText, EXCERPT_MAX_LENGTH),
    [annotation.messageText],
  );

  const handleSave = () => {
    void onUpdateNote(annotation.id, draftNote);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setDraftNote(annotation.note);
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsActionsMenuOpen(false);
    setIsEditing(true);
  };

  const handleDelete = () => {
    setIsActionsMenuOpen(false);
    onDelete(annotation);
  };

  return (
    <Card
      className="cursor-pointer gap-2"
      onClick={(event) => {
        if (shouldIgnoreCardNavigation(event.target)) {
          return;
        }

        onNavigate(annotation);
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) {
          return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onNavigate(annotation);
        }
      }}
    >
      <CardHeader className="items-start">
        <h3 className="m-0 text-sm font-bold text-foreground">{annotation.conversationName}</h3>
        {!isEditing ? (
          <DropdownMenu open={isActionsMenuOpen} onOpenChange={setIsActionsMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 min-h-0 px-0"
                aria-label="Annotation actions"
                onClick={() => {
                  setIsActionsMenuOpen((current) => !current);
                }}
              >
                <EllipsisVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleEdit}>
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDelete} className="text-destructive">
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </CardHeader>
      <CardContent className="gap-2.5">
        <p className="m-0 whitespace-pre-wrap break-words text-[0.8125rem] text-muted-foreground">
          {excerpt}
        </p>
        <div
          className="flex flex-col gap-2 rounded-md border border-border/70 bg-muted/30 p-2.5"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.04em] text-muted-foreground/80">
            Note
          </span>
          {isEditing ? (
            <>
              <Textarea
                value={draftNote}
                onChange={(event) => {
                  setDraftNote(event.target.value);
                }}
                placeholder="Add a note..."
                className="min-h-[4.5rem]"
              />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" className="w-full sm:w-auto" onClick={() => handleSave()}>
                  Save
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                  onClick={() => handleCancel()}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <p
              className={`m-0 whitespace-pre-wrap break-words text-[0.8125rem] ${annotation.note ? 'text-muted-foreground' : 'italic text-muted-foreground/70'}`}
            >
              {annotation.note || 'No note'}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
